import {Button} from '../../ui/Mobile';
import { DiaryPrivacy } from "../content/Content";
import { useEffect, useState } from "react";
import { Link } from "../../app/navigation";
import { useLiveQuery } from "dexie-react-hooks";
import { useService } from "../../app/context";
import { useOffline } from "../../app/offline";
import { Icon } from "../../ui/Icons";

const bytes = (n?: number) =>
  n == null ? "暂不可用" : `${(n / 1024 / 1024).toFixed(1)} MB`;
export function DataSettings() {
  const { db } = useService();
  const offline = useOffline();
  const [storage, setStorage] = useState<{
    quota?: number;
    usage?: number;
    persisted?: boolean;
    error?: string;
  }>({});
  const [requesting, setRequesting] = useState(false);
  const counts = useLiveQuery(
    async () => ({
      captures: await db.captures.count(),
      drafts:
        (await db.drafts.count()) +
        (await db.itemDrafts.count()) +
        (await db.contentDrafts.count()) +
        (await db.sopDrafts.count()) +
        (await db.manualStudyDrafts.count()),
      manualStudyPlans: await db.manualStudyPlans.count(),
      manualStudySessions: await db.manualStudySessions.count(),
      sopTemplates: await db.sopTemplates.count(),
      sopRuns: await db.sopRuns.count(),
      resources: await db.resources.count(),
      decisions: await db.decisions.count(),
      diaryEntries: await db.diaryEntries.count(),
      tasks: await db.tasks.count(),
      events: await db.events.count(),
      projects: await db.projects.count(),
      attachments: await db.attachments.count(),
    }),
    [db],
  );
  useEffect(() => {
    void (async () => {
      try {
        const estimate = await navigator.storage?.estimate?.();
        const persisted = await navigator.storage?.persisted?.();
        setStorage({ ...estimate, persisted });
      } catch {
        setStorage({ error: "设备未提供存储估计，保存时仍会检查实际结果。" });
      }
    })();
  }, []);
  async function requestPersistence() {
    setRequesting(true);
    try {
      const persisted = await navigator.storage.persist();
      setStorage((s) => ({
        ...s,
        persisted,
        error: persisted
          ? undefined
          : "浏览器未授予持久化。仍可使用，但请另行保留重要内容。",
      }));
    } catch {
      setStorage((s) => ({ ...s, error: "请求未完成，当前数据未被修改。" }));
    } finally {
      setRequesting(false);
    }
  }
  return (
    <main className="page settings-page" id="main">
      <Link className="back-link" to="/inbox">
        <Icon name="arrow" />
        返回收件箱
      </Link>
      <div className="eyebrow">保存在你的设备上</div>
      <h1>本机与离线</h1>
      <div className="detail-actions">
        <Link className="button primary" to="/settings/backup">
          备份与恢复
        </Link>
        <Link className="button secondary" to="/trash">
          回收站
        </Link>
      </div>
      <section>
        <h2>本机数据（含历史与回收站）</h2>
        <dl className="settings-list">
          <div>
            <dt>收件箱</dt>
            <dd>{counts?.captures ?? "读取中"} 条</dd>
          </div>
          <div>
            <dt>任务 / 事件 / 项目</dt>
            <dd>
              {counts?.tasks ?? '读取中'} / {counts?.events ?? '读取中'} /{" "}
              {counts?.projects ?? '读取中'}
            </dd>
          </div>
          <div>
            <dt>SOP 模板 / 执行</dt>
            <dd>
              {counts ? `${counts.sopTemplates} / ${counts.sopRuns}` : "读取中"}
            </dd>
          </div>
          <div>
            <dt>资料 / 决定 / 日记</dt>
            <dd>
              {counts
                ? `${counts.resources} / ${counts.decisions} / ${counts.diaryEntries}`
                : "读取中"}
            </dd>
          </div>
          <div>
            <dt>草稿</dt>
            <dd>{counts?.drafts ?? "读取中"} 份</dd>
          </div>
          <div>
            <dt>已保存图片</dt>
            <dd>{counts?.attachments ?? "读取中"} 张</dd>
          </div>
          <div>
            <dt>本来源存储估计</dt>
            <dd>{bytes(storage.usage)}</dd>
          </div>
          <div>
            <dt>浏览器配额估计</dt>
            <dd>{bytes(storage.quota)}</dd>
          </div>
          <div>
            <dt>持久化状态</dt>
            <dd>
              {storage.persisted === undefined
                ? "无法确认"
                : storage.persisted
                  ? "浏览器已授予"
                  : "尚未授予"}
            </dd>
          </div>
        </dl>
        <p className="small-note">
          配额不是实际可用磁盘保证。浏览器清理、卸载或更换访问地址可能影响本机数据；持久化也不等于外部备份。
        </p>
        {typeof navigator.storage?.persist === "function" &&
          !storage.persisted && (
            <Button
              className="button secondary"
              disabled={requesting}
              onClick={() => void requestPersistence()}
            >
              {requesting ? "正在请求…" : "申请持久化存储"}
            </Button>
          )}
        {storage.error && (
          <p className="notice" role="status">
            {storage.error}
          </p>
        )}
      </section>
      <section>
        <h2>离线使用</h2>
        <dl className="settings-list">
          <div>
            <dt>应用壳</dt>
            <dd>
              {offline.ready
                ? "已准备，可离线打开"
                : offline.error
                  ? "准备失败"
                  : import.meta.env.DEV
                    ? "开发模式未启用缓存"
                    : "正在准备"}
            </dd>
          </div>
          <div>
            <dt>网络提示</dt>
            <dd>{offline.online ? "设备报告已联网" : "设备报告离线"}</dd>
          </div>
        </dl>
        {offline.updateAvailable && (
          <p className="notice">
            新版已下载。请在内容保存后关闭所有 Nook
            窗口，再重新打开以更新；当前不会强制刷新。
          </p>
        )}
        <p>
          在 iPhone Safari
          的系统分享菜单中选择“添加到主屏幕”。安装前后是否共享数据取决于实际环境，目前尚未完成真机验证。
        </p>
      </section>
      <section>
        <h2>隐私与当前范围</h2>
        <p>
          正文和图片留在本机，不调用
          AI，不发送到分析服务。本机数据库未做应用层加密。
        </p>
        <p>
          这是 M6
          开发预览。已提供完整加密备份、校验、替换恢复、修改历史与回收站。其余里程碑尚未交付。iPhone
          已确认 M4
          日记草稿经一次锁屏重开后文字、图片、日期与搜索草稿状态正常；M3/M5/M6
          真机、强杀、低存储、断电等仍未验证。文本单独导出不能替代完整备份。
        </p>
      </section>
      <DiaryPrivacy />
    </main>
  );
}
